import {
  FormEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

export const useSearchForm = (
  items,
  selected,
  onChange: (item) => () => void
) => {
  const [focused, setFocused] = useState<number>(
    items.findIndex(({ value }) => selected && selected.value === value)
  )

  const [value, setValue] = useState<string>('')

  const filtered = useMemo(
    () =>
      value
        ? items.filter(
            item => item.value.toLowerCase().indexOf(value.toLowerCase()) >= 0
          )
        : items,
    [value, items]
  )

  const reset = useCallback(() => setValue(''), [])

  const submit = useCallback(
    (e?: FormEvent) => {
      if (e) {
        e.preventDefault()
      }

      if (focused >= 0 && filtered[focused]) {
        setValue('')
        onChange(filtered[focused])()
      }
    },
    [filtered, focused, onChange]
  )

  const keyDown = useCallback(
    (e: KeyboardEvent) => {
      let direction

      if (e.keyCode === 38 /* Arrow up */) {
        direction = -1
      } else if (e.keyCode === 40 /* Arrow down */) {
        direction = 1
      }

      if (direction && filtered.length) {
        if (focused >= 0) {
          setFocused((filtered.length + focused + direction) % filtered.length)
        } else if (direction > 0) {
          setFocused(0)
        } else {
          setFocused(filtered.length + direction)
        }
      }
    },
    [focused, filtered]
  )

  const change = useCallback((e: FormEvent<HTMLInputElement>) => {
    const value = e.target && (e.target as any).value

    if (typeof value === 'string') {
      setValue(value)
    }
  }, [])

  useEffect(() => {
    if (selected) {
      const focused = filtered.findIndex(item => item.value === selected.value)

      setFocused(focused)
    }
  }, [filtered, selected])

  return { focused, filtered, submit, keyDown, change, reset, value }
}
